import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import PlatformModalDialog from '@atlaskit/modal-dialog';
import { ModalProvider } from './ModalProvider';
import { adaptEventHandler } from '../../utils';
import type { OnCloseHandler } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * To simplify API surface and maintain consistent behaviour, the following props are excluded:
 * - onStackChange
 * - stackIndex
 * - isBlanketHidden
 * - shouldCloseOnOverlayClick
 * - shouldCloseOnEscapePress
 * - shouldReturnFocus
 * - onCloseComplete - behaviour can be replicated using `useEffect`
 * - onOpenComplete - behaviour can be replicated using `useEffect`
 */

type PlatformModalDialogProps = React.ComponentProps<typeof PlatformModalDialog>;

export type ModalProps = Pick<
	PlatformModalDialogProps,
	| 'autoFocus'
	| 'children'
	| 'height'
	| 'width'
	| 'onClose'
	| 'shouldScrollInViewport'
	| 'label'
	| 'testId'
>;

/**
 * A modal dialog displays content that requires user interaction, in a layer above the page.
 */
export const Modal = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { autoFocus, height, width, onClose, shouldScrollInViewport, label, testId } = props
		.forgeDoc.props as ModalProps;

	if (fg('forge-ui-datepicker-modal-fix')) {
		return (
			<ModalProvider>
				<PlatformModalDialog
					autoFocus={autoFocus}
					children={props.forgeDoc.children.map((c) => (
						<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
					))}
					height={height}
					width={width}
					onClose={adaptEventHandler(onClose) as OnCloseHandler}
					shouldScrollInViewport={shouldScrollInViewport}
					label={label}
					testId={testId}
				/>
			</ModalProvider>
		);
	}

	return (
		<PlatformModalDialog
			autoFocus={autoFocus}
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			height={height}
			width={width}
			onClose={adaptEventHandler(onClose) as OnCloseHandler}
			shouldScrollInViewport={shouldScrollInViewport}
			label={label}
			testId={testId}
		/>
	);
};
